// libraries.
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// store.
import { RootState } from "@store";
import { setIsSaving, updateException, updateIncentiveStep } from "./rulesSlice";

// interfaces.
import { ExceptionRule, IncentiveStep } from "@pages/RulesPage/modules/RulesMain/rulesData";

// mock service — swap por RTK Query mutation cuando exista el API.
const mockPatch = async <T>(data: T): Promise<T> => {
  await new Promise((resolve) => setTimeout(resolve, 700));
  return data;
};

const TOAST_IDS = {
  exception: "saveExceptionLoading",
  step: "saveStepLoading",
};

export const useRulesSlice = () => {
  const dispatch = useDispatch();

  const exceptions = useSelector(
    (state: RootState) => state.rulesReducer.exceptions,
  );
  const incentiveStepsCO = useSelector(
    (state: RootState) => state.rulesReducer.incentiveStepsCO,
  );
  const incentiveStepsMX = useSelector(
    (state: RootState) => state.rulesReducer.incentiveStepsMX,
  );
  const isSaving = useSelector(
    (state: RootState) => state.rulesReducer.isSaving,
  );

  const saveException = useCallback(
    async (updated: ExceptionRule) => {
      dispatch(setIsSaving(true));
      toast.loading("Guardando excepción...", {
        autoClose: false,
        toastId: TOAST_IDS.exception,
      });
      try {
        const saved = await mockPatch(updated);
        dispatch(updateException(saved));
        toast.dismiss(TOAST_IDS.exception);
        toast.success("Excepción actualizada correctamente");
      } catch {
        toast.dismiss(TOAST_IDS.exception);
        toast.error("Error al guardar la excepción");
      } finally {
        dispatch(setIsSaving(false));
      }
    },
    [dispatch],
  );

  const saveIncentiveStep = useCallback(
    async (country: string, updated: IncentiveStep) => {
      dispatch(setIsSaving(true));
      toast.loading("Guardando paso de incentivo...", {
        autoClose: false,
        toastId: TOAST_IDS.step,
      });
      try {
        const saved = await mockPatch(updated);
        dispatch(updateIncentiveStep({ country, step: saved }));
        toast.dismiss(TOAST_IDS.step);
        toast.success("Paso actualizado correctamente");
      } catch {
        toast.dismiss(TOAST_IDS.step);
        toast.error("Error al guardar el paso");
      } finally {
        dispatch(setIsSaving(false));
      }
    },
    [dispatch],
  );

  return {
    exceptions,
    incentiveStepsCO,
    incentiveStepsMX,
    isSaving,
    saveException,
    saveIncentiveStep,
  };
};

